/**
 * Chart collection loading.
 *
 * Where the tiles live is configuration, not a constant. GitHub Pages may turn
 * out not to serve HTTP range requests reliably, in which case the agreed
 * fallback is Cloudflare R2 — and that must be a URL change, not a code change.
 */

import { PMTiles, Protocol } from 'pmtiles';
import maplibregl from 'maplibre-gl';

import { cachedRangeSource, isChartCacheAvailable } from '../storage/chartCache';
import type { ChartManifest } from './style';

let protocol: Protocol | null = null;
const registeredArchives = new Set<string>();

/**
 * Teach MapLibre the `pmtiles://` scheme. Idempotent — MapLibre keeps protocol
 * handlers in module-level state, so registering twice would leak.
 */
export function registerPmtilesProtocol(): void {
  if (protocol) return;
  protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
}

/**
 * Point the protocol at cache-backed readers for a collection's archives.
 *
 * Without this the map fetches every byte range over the network even when the
 * skipper has downloaded the area — which would make the offline download
 * pointless. Registering a PMTiles instance whose Source reads Cache Storage
 * first means a downloaded area works with no signal at all.
 */
export function registerCachedArchives(manifest: ChartManifest, baseUrl = chartBaseUrl()): void {
  registerPmtilesProtocol();
  if (!protocol || !isChartCacheAvailable()) return;

  for (const source of manifest.sources) {
    const url = `${baseUrl}${source.file}`;
    if (registeredArchives.has(url)) continue;
    protocol.add(new PMTiles(cachedRangeSource(url)));
    registeredArchives.add(url);
  }
}

/**
 * Base URL for chart archives, with trailing slash.
 * `VITE_CHART_BASE_URL` points this at R2 or any other host without a rebuild
 * of application logic.
 */
export function chartBaseUrl(): string {
  const configured = import.meta.env.VITE_CHART_BASE_URL as string | undefined;
  if (configured) return configured.endsWith('/') ? configured : `${configured}/`;
  return `${import.meta.env.BASE_URL}charts/`;
}

/**
 * Glyph URL template for text rendering.
 *
 * Self-hosted rather than pointed at a font CDN: the app has to draw labels on a
 * boat with no signal, and a remote glyph URL would silently produce an
 * unlabelled chart exactly when it is least welcome. Populated by
 * `tools/fetch-glyphs.sh`.
 */
export function glyphsUrl(): string {
  return `${import.meta.env.BASE_URL}fonts/{fontstack}/{range}.pbf`;
}

export class ChartLoadError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'ChartLoadError';
  }
}

/**
 * Fetch a chart collection manifest.
 *
 * Failure here is not recoverable in the UI — without a manifest there is no
 * chart — so the error message is written for the skipper, not the developer.
 */
/**
 * Chart collections to load, in the order they should be drawn.
 *
 * `VITE_CHART_COLLECTIONS` is a comma-separated list of manifest ids. Adding a
 * region is therefore a configuration change plus a chart build — no code.
 */
export function configuredCollectionIds(): string[] {
  const configured = import.meta.env.VITE_CHART_COLLECTIONS as string | undefined;
  return (configured ?? 'v-atl-b')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

export async function loadChartManifest(id: string, baseUrl = chartBaseUrl()): Promise<ChartManifest> {
  const url = `${baseUrl}${id}.json`;
  let response: Response;
  try {
    response = await fetch(url);
  } catch (cause) {
    throw new ChartLoadError(
      'Could not reach the chart server. If you are offline, charts you have downloaded will still work.',
      { cause },
    );
  }
  if (!response.ok) {
    throw new ChartLoadError(`Chart collection "${id}" is not available (HTTP ${response.status}).`);
  }

  const manifest = (await response.json()) as ChartManifest;
  validateManifest(manifest);
  return manifest;
}

/** Reject a manifest that would produce a blank or misleading chart. */
export function validateManifest(manifest: ChartManifest): void {
  if (!manifest || typeof manifest !== 'object') {
    throw new ChartLoadError('Chart manifest is not readable.');
  }
  if (manifest.schemaVersion !== 1) {
    throw new ChartLoadError(
      `Chart data uses schema version ${manifest.schemaVersion}, which this version of the app cannot read.`,
    );
  }
  if (!Array.isArray(manifest.sources) || manifest.sources.length === 0) {
    throw new ChartLoadError('Chart manifest contains no chart data.');
  }
  for (const source of manifest.sources) {
    if (typeof source.file !== 'string' || !source.file) {
      throw new ChartLoadError('Chart manifest has a source with no file.');
    }
    if (!(source.minzoom < source.maxzoom)) {
      throw new ChartLoadError(`Chart band ${source.band} has an invalid zoom range.`);
    }
  }
}

/**
 * The largest zoom at which any band still has real tiles. Beyond this the map
 * is overzooming and the display must say so.
 */
export function maxChartedZoom(manifest: ChartManifest): number {
  return Math.max(...manifest.sources.map((s) => s.maxzoom));
}

export interface LoadedCollections {
  collections: ChartManifest[];
  /** Ids that failed, with the reason — surfaced rather than swallowed. */
  failures: Array<{ id: string; message: string }>;
}

/**
 * Load several chart collections.
 *
 * A collection that fails is reported, not thrown: with three regions loaded,
 * one bad manifest should not blank the chart for the other two. But the failure
 * must reach the skipper — silently showing two of three regions would look
 * exactly like water with no charted features.
 */
export async function loadChartCollections(
  ids: string[] = configuredCollectionIds(),
  baseUrl = chartBaseUrl(),
): Promise<LoadedCollections> {
  const settled = await Promise.allSettled(ids.map((id) => loadChartManifest(id, baseUrl)));

  const collections: ChartManifest[] = [];
  const failures: Array<{ id: string; message: string }> = [];

  settled.forEach((result, index) => {
    if (result.status === 'fulfilled') collections.push(result.value);
    else {
      failures.push({
        id: ids[index],
        message: result.reason instanceof Error ? result.reason.message : String(result.reason),
      });
    }
  });

  return { collections, failures };
}

/**
 * Flatten several collections into the single manifest shape the style builder
 * consumes.
 *
 * Source ids must stay unique across collections, so each source is tagged with
 * its collection id. Two collections both publishing a band 5 would otherwise
 * collide on `chart-band5` and one would silently replace the other.
 */
export function mergeCollections(collections: readonly ChartManifest[]): ChartManifest {
  const sources = collections.flatMap((collection) => collection.sources.map((source) => ({
    ...source,
    collectionId: collection.id,
  })));

  return {
    id: collections.map((c) => c.id).join('+') || 'none',
    name: collections.map((c) => c.name).join(', ') || 'No charts',
    schemaVersion: 1,
    layers: [...new Set(collections.flatMap((c) => c.layers))],
    sources,
  };
}

/** Circumference of the Earth at the equator, metres (WGS84). */
const EQUATORIAL_CIRCUMFERENCE_M = 40075016.686;

/**
 * Assumed physical size of a screen pixel, metres. 0.28 mm is the OGC standard
 * pixel used to define map scale denominators, so "1:40,000" here means the same
 * thing it means on a paper chart.
 */
const OGC_PIXEL_SIZE_M = 0.00028;

/**
 * Chart scale denominator at a given zoom and latitude — the N in "1:N".
 *
 * Mariners think in scale, not in zoom levels, and scale is what makes an
 * overzoom warning meaningful: a chart compiled at 1:20,000 displayed at
 * 1:5,000 is being stretched four times beyond its survey.
 *
 * MapLibre renders 512-pixel tiles, so the world is 512 * 2^zoom pixels wide.
 */
export function scaleDenominatorAt(zoom: number, latitude: number): number {
  const latitudeRadians = (latitude * Math.PI) / 180;
  const metresPerPixel = (EQUATORIAL_CIRCUMFERENCE_M * Math.cos(latitudeRadians))
    / (512 * 2 ** zoom);
  return metresPerPixel / OGC_PIXEL_SIZE_M;
}

/** Round a scale denominator to something a chart would actually print. */
export function formatScale(denominator: number): string {
  if (!Number.isFinite(denominator) || denominator <= 0) return '—';
  const rounded = denominator >= 100_000
    ? Math.round(denominator / 10_000) * 10_000
    : denominator >= 10_000
      ? Math.round(denominator / 1_000) * 1_000
      : Math.round(denominator / 100) * 100;
  return `1:${rounded.toLocaleString('en-US')}`;
}

/**
 * Whether the current view is beyond the largest-scale chart available.
 *
 * Over-scaling is a documented grounding cause: symbols do not grow with the
 * chart, so a danger stops covering the ground it actually occupies. The UI
 * shows a persistent warning whenever this is true.
 */
export function isOverzoomed(manifest: ChartManifest, zoom: number): boolean {
  return zoom > maxChartedZoom(manifest);
}
